import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { MdFilterList } from "react-icons/md";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { isoToBrFormat, getEmptyPageResponse, toTablePageableData } from "utils";
import { cameraService } from "services/cameraService";
import { ROUTES } from "constants/routes-path";

function CamerasList() {
  const { t} = useTranslation();
  const [searchParams, setSearchParams] = useState({ orderBy: "nome" });
  const [response, setResponse] = useState(undefined);
  const [selectedRow, setSelectedRow] = useState(undefined);
  const dialogRef = useRef(undefined);
  const showToast = useCustomToast();
  const navigate = useNavigate();

  const columns = useMemo(() => [
    { Header: t("settings.camera.list.table.header.name"), accessor: "nome" },
    { Header: t("settings.camera.list.table.header.brand"), accessor: "marca" },
    { Header: t("settings.camera.list.table.header.model"), accessor: "modelo" },
    {
      Header: t("settings.camera.list.table.header.installation-date"),
      accessor: "data_instalacao",
      custom: ({ value }) => isoToBrFormat(value),
    },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t("system.actions.edit"),
          icon: <FaEdit />,
          onClick: ({ original }) => navigate(ROUTES.camerasEditPath.replace(":id", original.id)),
        },
        {
          label: t("system.actions.remove"),
          icon: <FaTrashAlt color="#E53E3E" />,
          onClick: ({ original }) => handleRemove(original),
        }
      ]
    }
  ], []);

  const newCamera = () => {
    navigate(ROUTES.camerasAddPath);
  }

  const handleRemove = (row) => {
    setSelectedRow(row);
    dialogRef.current?.displayDialog({
      title: t("settings.camera.list.actions.remove-camera"),
      message: t("system.actions.remove-confirm"),
    });
  };

  const handleConfirmRemove = async () => {
    try {
      await cameraService.delete(selectedRow.id);
      showToast({ title: t("settings.camera.messages.success.delete"), status: "success" });
      await fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const fetchData = async (params = { orderBy: "nome" }) => {

    if (response) setResponse(undefined);

    try {
      params = { ...searchParams, ...params };
      setSearchParams(params);
      const data = await cameraService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
      <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
        <Button
          variant="list-primary"
          leftIcon={<AddIcon fontSize="sm" />}
          alignSelf={{ sm: "flex-start", lg: undefined }}
          onClick={newCamera}
        >
          {t("settings.camera.list.actions.new-camera")}
        </Button>

        <Stack
            direction="row"
            pt={{ sm: "20px", md: "0",  lg: "0" }}
            spacing="10px"
            alignSelf={{ sm: "flex-start", lg: "auto" }}
        >
          <Menu>
            <MenuButton
              disabled
              w="125px"
              fontSize="sm"
              fontWeight="bold"
              variant="light"
              leftIcon={<MdFilterList />}
              rightIcon={<IoIosArrowDown />}
              _hover={{ bg: "white" }}
              as={Button}
            >
              {t("system.labels.filters")}
            </MenuButton>

            <MenuList bg={useColorModeValue("white", "navy.800")}>
              <MenuItem color="gray.500">Status: Paid</MenuItem>
              <MenuItem color="gray.500">Status: Refunded</MenuItem>
              <MenuItem color="gray.500">Status: Canceled</MenuItem>
              <MenuDivider />
              <MenuItem color="red.300">Remove filter</MenuItem>
            </MenuList>
          </Menu>

          <Button
            disabled
            w="125px"
            fontSize="sm"
            fontWeight="bold"
            variant="light"
            _hover={{ bg: "white" }}
          >
            {t("settings.camera.list.actions.export")}
          </Button>
        </Stack>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("settings.camera.title")} subtitle={t("settings.camera.description")} />

        <CardBody>
          {response ? (
            <SearchTable2
              columnsData={columns}
              tableData={response.content}
              pageableData={toTablePageableData(response, { defaultField: "nome", ...searchParams })}
              onChange={fetchData}
            />
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>

      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Flex>
  );
}

export default CamerasList;
